import { captureException } from '@sentry/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FieldArray, Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { MdAdd, MdKeyboardArrowRight } from 'react-icons/md'

import type { ReactElement, ChangeEvent } from 'react'
import type { Tab } from '~/components/VerticalTabs'
import type { IAppSummary } from '~/types/application'
import type { IVersionEngineFormValues, IEngineMapping, IVersionEngine } from '~/types/arbitration'

import ActionHeader from '../components/ActionHeader'
import AddVersionsModal from '../components/AddVersions'
import EditVersions from '../components/EditVersions'
import EnginePlacements from '../components/EnginePlacements'
import { validationSchema } from '../utils'

import { addGroup, updateEngineGroup } from '~/api/arbitration'
import Button from '~/components/Buttons/Button'
import SearchInput from '~/components/Inputs/Basic/SearchInput'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import Label from '~/components/Label'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import VerticalTabs from '~/components/VerticalTabs'
import { DEFAULT_ITEMS_PER_PAGE_VALUE } from '~/const'
import { arbitrationKeys, useVersionArchiveEngineQuery } from '~/queries/arbitration'
import { useNavigationGraphsListQuery } from '~/queries/navigation-graphs'
import { usePlacementsQuery } from '~/queries/placements'
import { useGetMeAsUserQuery } from '~/queries/users'
import { ENGINE_TYPE } from '~/types/arbitration'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface Props {
  engineType: ENGINE_TYPE.ARCHIVE | ENGINE_TYPE.VERSION
  selectedApp: IAppSummary
}

const VersionArchiveEngine = ({ engineType, selectedApp }: Props): ReactElement => {
  const [placementSearch, setPlacementSearch] = useState('')

  const [versionSearch, setVersionSearch] = useState('')

  const [currentTab, setCurrentTab] = useState('')

  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false)

  const { data: user } = useGetMeAsUserQuery()

  const queryClient = useQueryClient()

  const { mutateAsync: addGroupMutation } = useMutation({ mutationFn: addGroup })

  const { mutateAsync: updateEngineGroupMutation } = useMutation({ mutationFn: updateEngineGroup })

  const { app_key: appKey, device_type: deviceType } = selectedApp

  const { data: engine, isLoading: isEngineLoading } = useVersionArchiveEngineQuery({ appKey, deviceType, engineType })

  const { data: navigationGraphs, isLoading: isNavigationGraphsLoading } = useNavigationGraphsListQuery(
    {
      'app-key': appKey,
      'device-type': deviceType,
      size: DEFAULT_ITEMS_PER_PAGE_VALUE,
    },
    { enabled: !!selectedApp }
  )

  const { data: placements, isLoading: isPlacementsLoading } = usePlacementsQuery({
    appKey,
    size: DEFAULT_ITEMS_PER_PAGE_VALUE,
  })

  const navigationGraphOptions =
    (navigationGraphs?.data && navigationGraphs.data.map(({ title, uuid }) => ({ label: title, value: uuid }))) || []

  const placementOptions = (placements?.data && placements.data.map(({ key }) => ({ label: key, value: key }))) || []

  const tabsData = (): Tab[] => {
    if (!engine) return []

    return (
      engine.map(({ app_versions, id }) => ({
        label: (
          <div className="version-engine-tab-label">
            <MdKeyboardArrowRight size={14} />
            <div>{app_versions?.map((version) => <div key={`${id}-${version}`}>{version}</div>) || 'No version'}</div>
          </div>
        ),
        labelClassName: !app_versions || app_versions.length === 0 ? 'no-version-tab-label' : '',
        name: id,
        tabId: `tab-${id}`,
      })) || []
    )
  }

  const handleAddGroup = async ({ appVersions, force }: { appVersions: string[]; force: boolean }) => {
    try {
      const { id } = await addGroupMutation({
        appKey,
        appVersions,
        deviceType,
        engineType,
        force,
        updatedBy: user?.user_id,
      })
      displayToast('Group successfully created', 'success')
      setIsAddGroupModalOpen(false)
      await queryClient.invalidateQueries({ queryKey: arbitrationKeys.engine(engineType, appKey, deviceType) })
      setCurrentTab(id)
    } catch (e) {
      displayToast('Group could not be created', 'error')
    }
  }

  const handleSubmit = async ({ force, placements, versions }: IVersionEngineFormValues) => {
    try {
      const mapping = placements.reduce((prev, { navigationGraph, placement }) => {
        if (navigationGraph && placement) {
          prev[placement.value] = navigationGraph.value
        }
        return prev
      }, {} as IEngineMapping)

      await updateEngineGroupMutation({
        appKey,
        appVersions: versions,
        deviceType,
        engineType,
        force,
        groupId: currentTab,
        mapping,
        updatedBy: user?.user_id,
      })

      displayToast('Engine successfully updated', 'success')
      queryClient.invalidateQueries({ queryKey: arbitrationKeys.engine(engineType, appKey, deviceType) })
    } catch (e) {
      displayToast('Engine could not be updated', 'error')
      captureException(e)
    }
  }

  const toggleAddGroupModal = (isOpen: boolean) => () => setIsAddGroupModalOpen(isOpen)

  const handleSearch = (evt: ChangeEvent<HTMLInputElement>) => {
    setPlacementSearch(evt.target.value)
  }
  const handleTabClick = (tabName: string) => {
    setCurrentTab(tabName)
  }

  const handleVersionSearch = (option: IOption | undefined) => {
    setVersionSearch(option?.value || '')
  }

  const getCurrentEngine = (): IVersionEngine | undefined => engine?.find(({ id }) => id === currentTab)

  const initialValues = (): IVersionEngineFormValues => {
    const engine = getCurrentEngine()

    return {
      force: false,
      placements: engine?.mapping
        ? Object.entries(engine.mapping).map(([placement, navigationGraph]) => ({
            navigationGraph: navigationGraphOptions.find(({ value }) => value === navigationGraph),
            placement: placementOptions.find(({ value }) => value === placement),
          }))
        : [],
      versions: engine?.app_versions || [],
    }
  }

  useEffect(() => {
    if (engine && engine.length > 0 && !currentTab) {
      setCurrentTab(engine[0].id)
    }
  }, [engine, currentTab])

  useEffect(() => {
    if (versionSearch && engine && engine.length > 0) {
      const groupId = engine.find(({ app_versions }) => app_versions?.includes(versionSearch))?.id
      groupId && setCurrentTab(groupId)
      document.getElementById(`tab-${groupId}`)?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [versionSearch, engine])

  if (isEngineLoading || isPlacementsLoading || isNavigationGraphsLoading) {
    return <MwMLoader category="item" />
  }

  const currentEngine = getCurrentEngine()

  const versionOptions =
    engine?.reduce((prev, { app_versions }) => {
      app_versions &&
        app_versions.forEach((version) => {
          prev.push({
            label: version,
            value: version,
          })
        })

      return prev
    }, [] as IOption[]) || []

  return (
    <Layout className="version-engine-view">
      <Formik
        enableReinitialize
        initialValues={initialValues()}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ dirty, handleSubmit, isSubmitting, resetForm }) => (
          <FieldArray
            name="placements"
            render={({ push, remove }) => (
              <Form className="engine-form">
                {currentEngine ? (
                  <>
                    <div className="engine-selector">
                      <SelectInput
                        isClearable
                        onChange={(option) => handleVersionSearch(option as IOption)}
                        options={versionOptions}
                        placeholder="Select version"
                        size="small"
                      />
                      <Button onClick={toggleAddGroupModal(true)} size="small">
                        <MdAdd color="#fff" size={16} /> Add group
                      </Button>
                      <VerticalTabs
                        currentTab={currentTab}
                        onClickTab={handleTabClick}
                        tabsData={tabsData()}
                        variant={engineType === ENGINE_TYPE.ARCHIVE ? 'primary' : 'secondary'}
                      />
                    </div>
                    <div className="engine-container">
                      <ActionHeader
                        addButtonText="Add group"
                        dirty={dirty}
                        isSubmitting={isSubmitting}
                        metadata={{ updatedAt: currentEngine.updated_at_ts, updatedBy: currentEngine.updated_by }}
                        onSave={handleSubmit}
                        onUndo={() => resetForm()}
                      />
                      <div className="engine-content">
                        <EditVersions appKey={appKey} deviceType={deviceType} engineType={engineType} />
                        <div className="engine-content__title">
                          <div className="engine-content__title__button">
                            <Label label="Placements" />
                            <Button
                              onClick={() => push({ navigationGraph: undefined, placement: undefined })}
                              size="small"
                            >
                              <MdAdd color="#fff" size={16} /> Add placement
                            </Button>
                          </div>
                          {currentEngine.mapping && Object.keys(currentEngine.mapping).length > 0 ? (
                            <div className="engine-content__title__search">
                              <SearchInput onChange={handleSearch} placeholder="Search by placement" size="small" />
                            </div>
                          ) : null}
                        </div>
                        <EnginePlacements
                          navigationGraphOptions={navigationGraphOptions}
                          onDelete={remove}
                          placementOptions={placementOptions}
                          search={placementSearch}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="no-engine-container">
                    <Button onClick={toggleAddGroupModal(true)}>
                      <MdAdd color="#fff" size={16} /> Add your first group
                    </Button>
                  </div>
                )}
                <AddVersionsModal
                  appKey={appKey}
                  deviceType={deviceType}
                  engineType={engineType}
                  isOpen={isAddGroupModalOpen}
                  onClose={toggleAddGroupModal(false)}
                  onSave={handleAddGroup}
                  title="Add group"
                />
              </Form>
            )}
          />
        )}
      </Formik>
    </Layout>
  )
}

export default VersionArchiveEngine
